import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCourierWizard from './AddCourierWizard';
import AgreementDialog from './AgreementDialog';
import ZonesDialog from './ZonesDialog';
import CourierSettlementDialog from './CourierSettlementDialog';
import CourierPayoutDialog from './CourierPayoutDialog';
import CourierReport from './CourierReport';
import {
  CourierKind,
  CourierPanelRow,
  CourierProfile,
  cashOf,
  courierModuleService,
  formatMoney,
} from './courierModuleService';
import type { CourierUiPermissions } from './courierPermissions';

interface Props {
  branchId: string;
  permissions: CourierUiPermissions;
}

type KindFilter = 'ALL' | 'OWN' | 'FREELANCE' | 'COMPANY' | 'PLATFORM_VALET';
const FILTERS: KindFilter[] = ['ALL', 'OWN', 'FREELANCE', 'COMPANY', 'PLATFORM_VALET'];

const kindColor = (k: CourierKind): 'info' | 'warning' | 'secondary' | 'default' =>
  k === 'OWN' ? 'info' : k === 'FREELANCE' ? 'warning' : k === 'COMPANY' ? 'secondary' : 'default';

/**
 * Şube yöneticisinin kurye ekranı: kendi şubesinin kuryeleri, bugünkü paket, ücret ve elindeki nakit.
 * Gün sonu, hakediş, rapor ve kurye ekleme/anlaşma ayrı alt yetkilerle açılır; sunucu da aynı kuralı uygular.
 */
const ManagerCourierPanel: React.FC<Props> = ({ branchId, permissions }) => {
  const { t } = useTranslation('salesSystem');
  const [rows, setRows] = useState<CourierPanelRow[]>([]);
  const [profiles, setProfiles] = useState<CourierProfile[]>([]);
  const [filter, setFilter] = useState<KindFilter>('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [zonesOpen, setZonesOpen] = useState(false);
  const [agreementCourier, setAgreementCourier] = useState<CourierProfile | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [panel, list] = await Promise.all([
        courierModuleService.panel(branchId),
        permissions.canManage ? courierModuleService.listCouriers() : Promise.resolve([] as CourierProfile[]),
      ]);
      setRows(panel);
      setProfiles(list);
    } catch (e: any) {
      setError(e?.message || t('courierModule.manager.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [branchId, permissions.canManage, t]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(
    () => rows.filter((r) => filter === 'ALL' || r.kind === filter),
    [rows, filter],
  );
  const packages = visible.reduce((s, r) => s + r.todayDelivered, 0);
  const cash = visible.filter((r) => r.kind !== 'PLATFORM_VALET').reduce((s, r) => s + cashOf(r), 0);
  const fees = visible.reduce((s, r) => s + r.todayFee, 0);

  if (reportOpen) {
    return <CourierReport lockedBranchId={branchId} onBack={() => setReportOpen(false)} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>{t('courierModule.manager.title')}</Typography>
        {permissions.canSettle && (
          <Button variant="contained" onClick={() => setSettlementOpen(true)}>{t('courierModule.settlement.open')}</Button>
        )}
        {permissions.canPayout && (
          <Button variant="outlined" onClick={() => setPayoutOpen(true)}>{t('courierModule.payout.open')}</Button>
        )}
        {permissions.canReport && (
          <Button variant="outlined" onClick={() => setReportOpen(true)}>{t('courierModule.report.title')}</Button>
        )}
        {permissions.canManage && (
          <>
            <Button variant="outlined" onClick={() => setZonesOpen(true)}>{t('courierModule.manager.zones')}</Button>
            <Button variant="outlined" onClick={() => setWizardOpen(true)}>{t('courierModule.manager.add')}</Button>
          </>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {FILTERS.map((f) => (
          <Chip key={f} label={t(`courierModule.manager.filters.${f}`)} color={filter === f ? 'primary' : 'default'}
            variant={filter === f ? 'filled' : 'outlined'} onClick={() => setFilter(f)} />
        ))}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 220px))' }, gap: 1.5, mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('courierModule.manager.kpiPackages')}</Typography>
          <Typography variant="h6" fontWeight={700}>{packages.toLocaleString('tr-TR')}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('courierModule.manager.kpiCash')}</Typography>
          <Typography variant="h6" fontWeight={700}>{formatMoney(cash)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('courierModule.manager.kpiFees')}</Typography>
          <Typography variant="h6" fontWeight={700}>{formatMoney(fees)}</Typography>
        </Paper>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
      {!loading && visible.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">{t('courierModule.manager.empty')}</Typography>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        {visible.map((r) => {
          const profile = profiles.find((p) => p.id === r.id);
          const feeWord = r.kind === 'OWN' ? t('courierModule.manager.bonus') : t('courierModule.manager.fee');
          return (
            <Card key={r.id} variant="outlined">
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>{r.name || `#${r.id}`}</Typography>
                  <Chip size="small" color={kindColor(r.kind)} label={t(`courierModule.manager.filters.${r.kind}`, { defaultValue: r.kind })} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('courierModule.manager.line', { count: r.todayDelivered, fee: formatMoney(r.todayFee), word: feeWord })}
                  {r.state === 'on_road' && r.inHandCount > 0 ? ` · ${t('courierModule.manager.onRoad', { count: r.inHandCount })}` : ''}
                </Typography>
                {r.kind !== 'PLATFORM_VALET' && r.collectsCash && (
                  <Typography variant="body2">
                    {t('courierModule.manager.cashInHand')} <b>{formatMoney(cashOf(r))}</b>
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  {r.failedCollections > 0 && (
                    <Chip size="small" color="warning" label={t('courierModule.manager.failed', { count: r.failedCollections })} />
                  )}
                  {permissions.canManage && profile && (
                    <Button size="small" onClick={() => setAgreementCourier(profile)}>{t('courierModule.manager.agreement')}</Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <CourierSettlementDialog open={settlementOpen} branchId={branchId} lockBranch
        onClose={() => setSettlementOpen(false)} onChanged={() => void load()} />
      <CourierPayoutDialog open={payoutOpen} lockedBranchId={branchId} onClose={() => setPayoutOpen(false)} />
      {permissions.canManage && (
        <>
          <ZonesDialog open={zonesOpen} onClose={() => setZonesOpen(false)} onChanged={() => void load()} />
          <AddCourierWizard
            open={wizardOpen}
            onClose={() => setWizardOpen(false)}
            onOwnSelected={() => { setWizardOpen(false); setSnack(t('courierModule.manager.ownHint')); }}
            existing={profiles}
            onCreated={(created, openAgreement) => {
              setWizardOpen(false);
              void load();
              if (openAgreement) setAgreementCourier(created);
            }}
          />
          <AgreementDialog
            open={!!agreementCourier}
            courier={agreementCourier}
            onClose={() => setAgreementCourier(null)}
            onSaved={() => { setAgreementCourier(null); setSnack(t('courierModule.snackbar.agreementSaved')); void load(); }}
            onOpenZones={() => { setAgreementCourier(null); setZonesOpen(true); }}
          />
        </>
      )}
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} message={snack || ''} />
    </Box>
  );
};

export default ManagerCourierPanel;
